<template>
  <v-container fluid class="pa-0 checklist-editor">
    <!-- Header with template name and settings -->
    <v-card tile flat>
      <v-container>
        <v-row v-if="template" class="mt-2">
          <v-col cols="6">
            <QTextField v-model="templateName" :label="'Name'" :externaledit="!loading" class="h3">
            </QTextField>
          </v-col>
          <v-col cols="4">
            <v-select v-model="template.TemplateType" :items="templateTypes" item-text="text" item-value="value"
              label="Template Type" :disabled="loading || !template" @change="updateTemplate">
              <template v-slot:append-outer>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" small>mdi-information</v-icon>
                  </template>
                  <span>Determines where this checklist appears in the job workflow</span>
                </v-tooltip>
              </template>
            </v-select>
          </v-col>
          <v-col cols="2">
            <v-text-field v-model="template.SortOrder" label="Sort Order" type="number" :disabled="loading || !template"
              @change="updateTemplate">
              <template v-slot:append-outer>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" small>mdi-information</v-icon>
                  </template>
                  <span>Controls the order of this template within its section. Lower numbers appear first.</span>
                </v-tooltip>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <!-- Main Editor Layout -->
    <v-card>
      <v-row v-if="template" class="ma-0">
        <!-- Left Panel - Components -->
        <v-col cols="3" class="pa-0">
          <v-card tile class="h-100 components-panel" flat outlined>
            <v-card-title class="text-h6">
              Components
            </v-card-title>
            <v-card-text>
              <p class="text-caption grey--text mb-4">
                Drag items from here onto the list to add elements.
              </p>

              <!-- Draggable Components -->
              <div class="component-item" v-for="fieldType in fieldTypes" :key="fieldType.value" draggable="true"
                @dragstart="onDragStart($event, fieldType)">
                <v-card outlined class="component-card">
                  <div class="d-flex align-center pa-3">
                    <v-icon class="mr-3" color="primary">{{ fieldType.icon }}</v-icon>
                    <span class="text-body-1">{{ fieldType.text }}</span>
                  </div>
                </v-card>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Center Panel - Checklist Preview -->
        <v-col cols="6" class="pa-0">
          <v-card tile class="h-100 checklist-preview" flat outlined>
            <v-card-title class="text-h6">
              {{ templateName || 'Checklist.title' }}
            </v-card-title>
            <v-card-text>
              <!-- Drop zone for checklist items -->
              <div class="checklist-dropzone" @dragover="onDragOver" @drop="onDrop">

                <!-- Empty state -->
                <div v-if="fieldlist.length === 0" class="text-center pa-8">
                  <v-icon size="48" color="grey lighten-1">mdi-format-list-bulleted</v-icon>
                  <p class="text-h6 grey--text mt-4">No fields yet</p>
                  <p class="text-body-2 grey--text">Drag components here or click "Add" to create your first field</p>
                </div>

                <!-- Checklist Items -->
                <DxSortable v-else :data="fieldlist" :allow-reordering="true" item-orientation="vertical"
                  @reorder="onListReorder">
                  <template v-for="(item, index) in fieldlist">
                    <v-card :key="item.Id" class="ma-2 checklist-item" :color="!item.Deleted ? '' : 'grey'"
                      @click="selectItem(item)" :class="{ 'selected-item': highlightedItem === item.Id }">
                      <v-row no-gutters align="center">
                        <v-col cols="auto" class="pr-2">
                          <v-btn icon small class="handle">
                            <v-icon>mdi-drag-vertical</v-icon>
                          </v-btn>
                        </v-col>
                        <v-col>
                          <div class="d-flex align-center">
                            <v-icon class="mr-2" color="primary">
                              {{ getFieldTypeInfo(item.Type).icon }}
                            </v-icon>
                            <div>
                              <div class="text-subtitle-2 font-weight-medium">
                                {{ item.Name ? item.Name : ('field ' + item.Id) }}
                              </div>
                              <div class="text-caption grey--text">
                                {{ getFieldTypeInfo(item.Type).text }}
                                <span v-if="item.Section" class="ml-2">� {{ item.Section }}</span>
                              </div>
                            </div>
                          </div>
                        </v-col>
                        <v-col cols="auto">
                          <v-btn icon small @click.stop="deleteItem(index)">
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-card>
                  </template>
                </DxSortable>
              </div>

              <!-- Add Button -->
              <div class="d-flex justify-end mt-4">
                <v-btn color="primary" @click="addBlank()">
                  <v-icon size="17">mdi-plus</v-icon> Add
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Right Panel - Field Properties -->
        <v-col cols="3" class="pa-0">
          <v-card tile class="h-100 properties-panel" flat outlined>
            <v-card-title class="text-h6">
              Field Properties
            </v-card-title>
            <v-card-text v-if="selectedField">
              <!-- Field Properties Form -->
              <v-form>
                <v-text-field v-model="selectedField.Name" label="Label" @change="updateSelectedField"
                  :placeholder="'field ' + selectedField.Id">
                </v-text-field>

                <v-select v-model="selectedField.Type" :items="fieldTypes" item-text="text" item-value="value"
                  label="Type" @change="updateSelectedField">
                  <template v-slot:selection="{ item }">
                    <v-icon left>{{ item.icon }}</v-icon>
                    {{ item.text }}
                  </template>
                  <template v-slot:item="{ item }">
                    <v-icon left>{{ item.icon }}</v-icon>
                    {{ item.text }}
                  </template>
                </v-select>

                <!-- Value Options for Dropdown/Radio -->
                <div v-if="selectedField.Type === 2 || selectedField.Type === 3" class="mt-4">
                  <div class="text-subtitle-2 mb-2">Value options</div>
                  <div v-if="selectedField.Condition && choiceItems(selectedField.Condition).length > 0" class="mb-2">
                    <v-chip v-for="choice in choiceItems(selectedField.Condition)" :key="choice" small color="primary"
                      class="mr-2 mb-1">
                      {{ choice }}
                      <v-icon right small @click="removeChoice(selectedField, choice)" class="ml-1">mdi-close</v-icon>
                    </v-chip>
                  </div>
                  <v-text-field label="Add option" v-model="choiceText" @keypress.enter="addChoice(selectedField)"
                    append-icon="mdi-plus" @click:append="addChoice(selectedField)"
                    hint="Press Enter or click + to add option">
                  </v-text-field>
                </div>

                <v-checkbox v-model="selectedField.AllowComment" @change="updateSelectedField" label="Allow comment">
                </v-checkbox>

                <v-text-field v-model="selectedField.SortOrder" label="Sort order" type="number"
                  @change="updateSelectedField">
                </v-text-field>

                <v-combobox v-model="selectedField.Section" :items="distinctSections" label="Section"
                  @change="updateSelectedField">
                </v-combobox>
              </v-form>
            </v-card-text>
            <v-card-text v-else class="text-center pa-8">
              <v-icon size="48" color="grey lighten-1">mdi-cog</v-icon>
              <p class="text-h6 grey--text mt-4">No field selected</p>
              <p class="text-body-2 grey--text">Click on a field to edit its properties</p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>


<script>
import ClientAPIMixin from "@/api/ClientAPIMixin";
import DxSortable from "devextreme-vue/sortable";
import Vue from "vue";

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    return new Promise((resolve, reject) => {
      const later = () => {
        clearTimeout(timeout);
        func.apply(this, args)
          .then(resolve)
          .catch(reject);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    });
  };
}

export default {
  name: "EditCheckList",
  components: {
    DxSortable,
  },
  mixins: [ClientAPIMixin],

  data() {
    return {
      choiceText: "",
      template: {
        TemplateType: 1,
        SortOrder: 0
      },
      // Debounced update methods
      debouncedUpdateTemplate: null,
      debouncedUpdateCheckListField: null,
      fieldlist: [],
      fieldTypes: [
        { text: "Checkbox", value: 1, icon: "mdi-checkbox-marked-outline" },
        { text: "Dropdown List", value: 2, icon: "mdi-format-list-bulleted" },
        { text: "Radio Button Group", value: 3, icon: "mdi-radiobox-marked" },
        { text: "Number", value: 4, icon: "mdi-numeric" },
        { text: "Text", value: 5, icon: "mdi-text" }
      ],
      templateName: "",
      highlightedItem: null,
      selectedField: null,
      loading: true,

      templateTypes: [
        { text: "Job Header", value: 1 },
        { text: "Machines", value: 2 },
        { text: "Job Footer", value: 3 }
      ],
    };
  },

  mounted() {
    // Initialize debounced methods
    this.debouncedUpdateTemplate = debounce(this.updateCheckListTemplate, 2000);
    this.debouncedUpdateCheckListField = debounce(this.updateCheckListField, 1000);

    this.loading = true;
    this.getCheckListTemplate(this.$route.params.templateId)
      .then((templateResponse) => {
        // Ensure templateResponse is not null
        if (!templateResponse) {
          console.error('Template response is null');
          this.template = {
            TemplateType: 1,
            SortOrder: 0,
            Name: '',
            CheckListFields: [],
          };
          return;
        }

        // Initialize template with reactive properties
        this.template = {
          ...templateResponse,
          TemplateType: templateResponse.TemplateType || 1,
          SortOrder: templateResponse.SortOrder || 0,
        };

        console.log('Template loaded:', this.template); // Debug log
        console.log('TemplateType:', this.template.TemplateType); // Debug log

        this.templateName = templateResponse.Name || '';
        this.fieldlist = (templateResponse.CheckListFields || []).map((e) => {
          Vue.set(e, "loading", false);

          // Convert legacy string format to JSON for dropdown/radio fields
          if ((e.Type === 2 || e.Type === 3) && e.Condition && typeof e.Condition === 'string') {
            const choices = this.choiceItems(e.Condition);
            e.Condition = this.convertChoicesToJson(choices);
          }

          return e;
        });


        // Ensure DOM is updated after data is set
        this.$nextTick(() => {
          console.log('After nextTick - TemplateType:', this.template.TemplateType);
        });
      })
      .catch((error) => {
        console.error('Error loading template:', error);
        // Set default template on error
        this.template = {
          TemplateType: 1,
          SortOrder: 0,
          Name: '',
          CheckListFields: [],
        };
      })
      .finally(() => {
        this.loading = false;
      });
  },

  methods: {
    addChoice(item) {
      //console.log("AddChoice", item);
      if (this.choiceText.trim()) {
        const choices = this.choiceItems(item.Condition);
        choices.push(this.choiceText.trim());
        item.Condition = this.convertChoicesToJson(choices);
        this.choiceText = ""; // Clear the input
        this.updateField(this.fieldlist.indexOf(item));
      }
    },

    removeChoice(item, choiceToRemove) {
      if (item.Condition) {
        const choices = this.choiceItems(item.Condition);
        const filteredChoices = choices.filter(choice => choice !== choiceToRemove);
        item.Condition = this.convertChoicesToJson(filteredChoices);
        this.updateField(this.fieldlist.indexOf(item));
      }
    },

    choiceItems(items) {
      if (!items) return [];

      // Handle both string format (legacy) and JSON format
      if (typeof items === 'string') {
        if (items.indexOf(";") > 0) {
          return items.split(";");
        } else if (items.trim()) {
          return [items];
        }
        return [];
      }

      // Handle JSON format
      if (typeof items === 'object' && items.group) {
        return items.group.map(option => option.name);
      }

      return [];
    },

    convertChoicesToJson(choices) {
      if (!choices || choices.length === 0) return null;

      return {
        inline: false,
        group: choices.map(choice => ({
          name: choice,
          value: choice
        }))
      };
    },

    updateField(index) {
      //console.log("Updating field", index);
      const item = this.fieldlist[index];

      // Clear choices if switching away from dropdown/radiogroup types
      if (item.Type !== 2 && item.Type !== 3 && item.Condition) {
        item.Condition = null;
      }

      // Convert legacy string format to JSON if needed
      if ((item.Type === 2 || item.Type === 3) && item.Condition && typeof item.Condition === 'string') {
        const choices = this.choiceItems(item.Condition);
        item.Condition = this.convertChoicesToJson(choices);
      }

      Vue.set(this.fieldlist[index], "loading", true);
      this.debouncedUpdateCheckListField(item)
        .then((response) => {
          //console.log("Done", this.fieldlist[index]);
          Vue.set(this.fieldlist[index], response);
        })
        .catch((error) => {
          console.error("Error updating field:", error);
        })
        .finally(() => {
          Vue.set(this.fieldlist[index], "loading", false);
        });
    },

    addBlank() {
      this.addCheckListTemplateField(this.template.Id).then((response) => {
        // Set default type to checkbox (value 1)
        response.Type = 1;
        this.fieldlist.push(response);
        // Auto-highlight the new field for editing
        this.highlightedItem = response.Id;
        this.selectedField = response;
      });
    },

    deleteItem(index) {
      var item = this.fieldlist[index];
      item.Deleted = true;
      this.debouncedUpdateCheckListField(item)
        .catch((error) => {
          console.error("Error deleting field:", error);
        })
        .finally(() => {
          this.fieldlist.splice(index, 1);
        });
    },

    onListReorder(e) {
      const list = this.fieldlist.splice(e.fromIndex, 1)[0];
      this.fieldlist.splice(e.toIndex, 0, list);

      // Update sort order for all fields after reordering
      this.fieldlist.forEach((field, index) => {
        if (field.SortOrder !== index) {
          field.SortOrder = index;
          this.debouncedUpdateCheckListField(field)
            .catch((error) => {
              console.error("Error updating field sort order:", error);
            });
        }
      });
    },

    updateTemplate() {
      this.debouncedUpdateTemplate(this.template);
    },

    // New methods for the updated layout
    selectItem(item) {
      this.highlightedItem = item.Id;
      this.selectedField = item;
    },

    updateSelectedField() {
      if (this.selectedField) {
        const index = this.fieldlist.findIndex(field => field.Id === this.selectedField.Id);
        if (index !== -1) {
          // Convert legacy string format to JSON if needed
          if ((this.selectedField.Type === 2 || this.selectedField.Type === 3) &&
            this.selectedField.Condition && typeof this.selectedField.Condition === 'string') {
            const choices = this.choiceItems(this.selectedField.Condition);
            this.selectedField.Condition = this.convertChoicesToJson(choices);
          }
          this.updateField(index);
        }
      }
    },

    onDragStart(event, fieldType) {
      event.dataTransfer.setData('text/plain', JSON.stringify(fieldType));
      event.dataTransfer.effectAllowed = 'copy';
    },

    onDragOver(event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    },

    onDrop(event) {
      event.preventDefault();
      const fieldTypeData = event.dataTransfer.getData('text/plain');
      if (fieldTypeData) {
        const fieldType = JSON.parse(fieldTypeData);
        this.addFieldFromDrag(fieldType);
      }
    },

    addFieldFromDrag(fieldType) {
      this.addCheckListTemplateField(this.template.Id).then((response) => {
        response.Type = fieldType.value;

        // Initialize with empty JSON structure for dropdown/radio fields
        if (fieldType.value === 2 || fieldType.value === 3) {
          response.Condition = this.convertChoicesToJson([]);
        }

        this.fieldlist.push(response);
        this.selectItem(response);
      });
    },


  },

  computed: {
    distinctSections() {
      const d = this.fieldlist.map((item) => item.Section).filter((e) => e != null);
      const unique = [...new Set(d)];
      //console.log("DistinctSections:", unique);
      return unique;
    },
    getFieldTypeInfo() {
      return (typeValue) => {
        const fieldType = this.fieldTypes.find(ft => ft.value === typeValue);
        return fieldType || { text: 'Unknown', icon: 'mdi-help-circle' };
      };
    },
    templateTypeValue() {
      return this.template && this.template.TemplateType !== undefined ? this.template.TemplateType : 1;
    },
    contractTypeList() {
      return this.$store.getters["LookUpStore/ContractTypeList"] || [];
    },
    jobPriorityList() {
      return this.$store.getters["LookUpStore/JobPriorityList"] || [];
    },
  },

  watch: {
    templateName(newName) {
      if (this.template && newName !== this.template.Name) {
        this.template.Name = newName;
        this.updateTemplate();
      }
    },
    'template.TemplateType': {
      handler(newValue) {
        console.log('TemplateType changed to:', newValue); // Debug log
      },
      immediate: true
    }
  },
};
</script>

<style lang="scss" scoped>
.q-input.h3 textarea {
  color: #5f5f5f !important;
}

.checklist-editor {
  height: calc(100vh - 200px);
}

.components-panel,
.checklist-preview,
.properties-panel {
  height: 100%;
  overflow-y: auto;
}

.component-item {
  margin-bottom: 8px;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}

.component-card {
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }
}

.checklist-dropzone {
  min-height: 200px;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #1976d2;
    background-color: rgba(25, 118, 210, 0.04);
  }
}

.checklist-item {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  &.selected-item {
    border-color: #1976d2;
    background-color: rgba(25, 118, 210, 0.04);
  }
}

.properties-panel {
  .v-card__text {
    padding: 16px;
  }
}

.handle {
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}
</style>
